(() => {
  'use strict';

  document.documentElement.dataset.energyAssistantScript = 'loaded';

  const initEnergyAssistant = () => {
    const root = document.querySelector('[data-energy-assistant]');
    if (!root || root.dataset.assistantBound === 'true') return;

    const panel = root.querySelector('#EnergyAssistantPanel');
    const launcher = root.querySelector('[data-assistant-launcher]');
    const closeButton = root.querySelector('[data-assistant-close]');
    const resetButton = root.querySelector('[data-assistant-reset]');
    const teaser = root.querySelector('[data-assistant-teaser]');
    const teaserOpen = root.querySelector('[data-assistant-teaser-open]');
    const teaserClose = root.querySelector('[data-assistant-teaser-close]');
    const log = root.querySelector('[data-assistant-log]');
    const form = root.querySelector('[data-assistant-form]');
    const input = root.querySelector('[data-assistant-input]');
    const progress = root.querySelector('[data-assistant-progress]');
    const progressBar = root.querySelector('[data-assistant-progress-bar]');
    const progressLabel = root.querySelector('[data-assistant-progress-label]');
    const status = root.querySelector('[data-assistant-status]');
    if (!panel || !launcher || !closeButton || !log || !form || !input) return;
    const mobileMedia = window.matchMedia('(max-width: 640px), (max-height: 520px) and (pointer: coarse)');

    const routes = {
      home: root.dataset.homeUrl || '/pages/energy-packages',
      business: root.dataset.businessUrl || '/pages/small-business-energy-packages',
      commercial: root.dataset.commercialUrl || '/pages/commercial-performance',
      contact: root.dataset.contactUrl || '/pages/contact',
      data: root.dataset.dataUrl || '/pages/connect-energy-data',
      industries: root.dataset.industriesUrl || '/pages/industries',
      process: root.dataset.processUrl || '/pages/how-it-works',
      risen: root.dataset.risenUrl || '/pages/risen',
      dyness: root.dataset.dynessUrl || '/pages/dyness',
      solis: root.dataset.solisUrl || '/pages/solis',
      solinteg: root.dataset.solintegUrl || '/pages/solinteg',
      search: root.dataset.searchUrl || '/search'
    };

    const state = {
      assessment: null,
      lastFocused: null,
      unsupportedCount: 0,
      sessionMessages: 0,
      teaserTimer: null,
      interacted: false,
      inertedElements: new Set()
    };

    const assessmentQuestions = {
      audience: {
        key: 'audience',
        question: 'Which type of site are we assessing?',
        options: [
          ['Home', 'home'],
          ['Small business', 'small-business'],
          ['Commercial, industrial or multi-site', 'commercial']
        ]
      },
      goal: {
        key: 'goal',
        question: answers => answers.audience === 'home'
          ? 'What is the main outcome you want for your home?'
          : answers.audience === 'small-business'
            ? 'What is the main outcome you want for the business?'
            : 'What is the main outcome for the site or portfolio?',
        options: answers => {
          if (answers.audience === 'home') return [
            ['Lower electricity bills', 'lower bills'],
            ['Add a battery', 'battery'],
            ['Backup during outages', 'backup'],
            ['Prepare for EVs or electrification', 'future loads'],
            ['Check an existing system', 'existing performance'],
            ['Not sure yet', 'unsure']
          ];
          if (answers.audience === 'small-business') return [
            ['Reduce operating costs', 'lower operating costs'],
            ['Manage demand charges', 'demand charges'],
            ['Add backup or resilience', 'business resilience'],
            ['Prepare for growth or EVs', 'growth and EVs'],
            ['Review an existing system', 'existing performance'],
            ['Build an investment case', 'investment case']
          ];
          return [
            ['National or multi-site rollout', 'multi-site rollout'],
            ['Reduce energy and demand costs', 'energy and demand costs'],
            ['Solar and battery infrastructure', 'solar and battery infrastructure'],
            ['Resilience and continuity', 'resilience and continuity'],
            ['Electrification and EV charging', 'electrification and EVs'],
            ['Investment and reporting', 'investment and reporting']
          ];
        }
      },
      existing: {
        key: 'existing',
        question: 'What is installed today?',
        options: [
          ['No solar or battery', 'none'],
          ['Solar only', 'solar only'],
          ['Solar and battery', 'solar and battery'],
          ['A project is already planned', 'project planned'],
          ['Not sure', 'unknown']
        ]
      },
      data: {
        key: 'data',
        question: 'What energy information is available?',
        options: [
          ['Recent electricity bills', 'bills'],
          ['Interval or smart-meter data', 'interval data'],
          ['Bills and interval data', 'bills and interval data'],
          ['Nothing yet / not sure', 'not ready']
        ]
      },
      detail: {
        key: 'detail',
        question: answers => answers.audience === 'home'
          ? 'What size is the home?'
          : answers.audience === 'small-business'
            ? 'Which operating profile is closest?'
            : 'What scale should the first assessment cover?',
        options: answers => {
          if (answers.audience === 'home') return [
            ['3 bedrooms', '3 bedrooms'],
            ['4 bedrooms', '4 bedrooms'],
            ['5 bedrooms', '5 bedrooms'],
            ['6+ bedrooms', '6 or more bedrooms']
          ];
          if (answers.audience === 'small-business') return [
            ['Retail or trade store', 'retail or trade store'],
            ['Refrigeration, food or hospitality', 'refrigeration, food or hospitality'],
            ['Warehouse or logistics', 'warehouse or logistics'],
            ['Factory or manufacturing', 'factory or manufacturing'],
            ['Office, strata or other', 'office, strata or other']
          ];
          return [
            ['One commercial site', 'one commercial site'],
            ['2–10 sites', '2 to 10 sites'],
            ['11–50 sites', '11 to 50 sites'],
            ['More than 50 sites', 'more than 50 sites']
          ];
        }
      },
      supply: {
        key: 'supply',
        question: answers => answers.audience === 'home'
          ? 'Do you know the home’s electricity supply?'
          : 'Do you know the site’s electricity supply?',
        options: answers => answers.audience === 'commercial'
          ? [
              ['Three-phase', 'three-phase'],
              ['Multiple supplies or NMIs', 'multiple supplies'],
              ['Not sure', 'unknown']
            ]
          : [
              ['Single-phase', 'single-phase'],
              ['Three-phase', 'three-phase'],
              ['Not sure', 'unknown']
            ]
      }
    };

    const assessmentOrder = ['audience', 'goal', 'existing', 'data', 'detail', 'supply'];

    const knowledge = {
      home: {
        message: 'For homes, the quickest route is our guided selector. It compares solar-and-battery and battery-only starting packages for 3–6+ bedroom homes, EV needs and single- or three-phase supply. Final design still requires bills, site checks and network confirmation.',
        actions: [
          { label: 'Find my home package', href: routes.home, primary: true },
          { label: 'Run a 2-minute assessment', command: 'assessment' },
          { label: 'Battery only', topic: 'battery' }
        ]
      },
      business: {
        message: 'Our small-business selector covers starting configurations up to 100 kW solar and 200 kWh battery. It considers operating hours, spend, supply, major loads, backup needs, existing equipment and installation space.',
        actions: [
          { label: 'Start business selector', href: routes.business, primary: true },
          { label: 'Run a 2-minute assessment', command: 'assessment' },
          { label: 'Explore industries', href: routes.industries }
        ]
      },
      commercial: {
        message: 'For commercial, industrial and multi-site customers, Moebotics Energy can coordinate data review, site prioritisation, design, delivery, reporting and ongoing optimisation. The first step is to define portfolio scale, load profile, tariffs, site constraints and investment criteria.',
        actions: [
          { label: 'Qualify my project', command: 'assessment', primary: true },
          { label: 'Commercial capabilities', href: routes.commercial },
          { label: 'Request a specialist', topic: 'callback-commercial' }
        ]
      },
      battery: {
        message: 'Battery-only and solar-plus-battery pathways are available. Useful sizing depends on usage timing, existing solar, inverter compatibility, supply phase, peak demand, export limits and the loads you want backed up. Capacity should follow the evidence—not be selected in isolation.',
        actions: [
          { label: 'Home battery pathway', href: routes.home, primary: true },
          { label: 'Business battery pathway', href: routes.business },
          { label: 'Dyness batteries', href: routes.dyness }
        ]
      },
      solar: {
        message: 'A useful solar design matches roof and electrical capacity with daytime consumption, export constraints and future loads. Panel count alone does not determine value; orientation, shading, inverter design, network approval and operating profile all matter.',
        actions: [
          { label: 'Risen solar panels', href: routes.risen },
          { label: 'Home packages', href: routes.home, primary: true },
          { label: 'Business assessment', href: routes.business }
        ]
      },
      phase: {
        message: 'If you do not know whether the property is single-phase or three-phase, choose “not sure”. The home selector uses single-phase only as an initial assumption. Supply, switchboard capacity and inverter design must be confirmed before a final proposal.',
        actions: [
          { label: 'Start home selector', href: routes.home, primary: true },
          { label: 'Start guided assessment', command: 'assessment' }
        ]
      },
      incentives: {
        message: 'Where eligible, a proposal can separate solar STCs, battery STCs, NSW PRCs and optional VPP benefits. Programs, eligibility and certificate values can change, so the final amount must be confirmed for the site, equipment and installation date—never assumed from a headline figure.',
        actions: [
          { label: 'Check a home pathway', href: routes.home, primary: true },
          { label: 'Check a business pathway', href: routes.business },
          { label: 'Ask a specialist', topic: 'callback' }
        ]
      },
      cashback: {
        message: 'Ask our team whether the $500 contact-detail cashback promotion is currently available. Availability, eligibility, offer period and terms must be confirmed before you rely on the offer.',
        actions: [
          { label: 'Check eligibility with the team', topic: 'callback', primary: true },
          { label: 'View home packages', href: routes.home }
        ]
      },
      data: {
        message: 'Bills show tariffs and total cost; interval data shows when energy is imported, used and exported. With your permission, those inputs support better sizing, tariff analysis and scenario modelling. Never share retailer passwords in chat.',
        actions: [
          { label: 'Connect energy data', href: routes.data, primary: true },
          { label: 'How our process works', href: routes.process },
          { label: 'Request help', topic: 'callback' }
        ]
      },
      savings: {
        message: 'Savings, price and payback cannot be confirmed from system size alone. They depend on usage timing, tariffs, export limits, network approval, equipment, installation scope, finance and operating strategy. We show assumptions and compare options before a final recommendation.',
        actions: [
          { label: 'Get a starting package', command: 'assessment', primary: true },
          { label: 'Connect energy data', href: routes.data },
          { label: 'Request an assessment', topic: 'callback' }
        ]
      },
      estimate: {
        message: 'Website packages and chatbot guidance are estimates only. Final pricing is subject to a complete assessment and site inspection. Additional cabling, switchboard work, structural engineering, network requirements, access equipment, DA approval or other site-specific work may add cost.',
        actions: [
          { label: 'Run guided assessment', command: 'assessment', primary: true },
          { label: 'Talk to the team', topic: 'callback' }
        ]
      },
      brands: {
        message: 'Predefined home packages use Risen 475 W panels, Solis inverters and Dyness STACK100 Pro batteries. Commercial equipment is selected for confirmed system size, electrical design, site conditions and compatibility.',
        actions: [
          { label: 'Risen', href: routes.risen },
          { label: 'Dyness', href: routes.dyness },
          { label: 'Solis', href: routes.solis },
          { label: 'Solinteg', href: routes.solinteg }
        ]
      },
      monitoring: {
        message: 'Moebotics Energy can support reporting, performance review and ongoing optimisation after installation. The exact monitoring scope, data access, service frequency and term are confirmed in the selected service agreement.',
        actions: [
          { label: 'See our process', href: routes.process, primary: true },
          { label: 'Commercial capabilities', href: routes.commercial },
          { label: 'Ask about managed service', topic: 'callback-commercial' }
        ]
      },
      ev: {
        message: 'EV charging can materially change when and how much electricity a site uses. We consider vehicle count, charger power, charging windows, supply capacity, solar production and battery strategy so the infrastructure can grow without creating avoidable peaks.',
        actions: [
          { label: 'Home with EVs', href: routes.home, primary: true },
          { label: 'Business or fleet charging', topic: 'callback-commercial' },
          { label: 'Run guided assessment', command: 'assessment' }
        ]
      },
      industries: {
        message: 'Solar, batteries and energy management can be valuable for logistics, distribution, supermarkets, trade stores, factories, food processing, petrol stations, restaurants, strata and other energy-intensive sites. The best opportunity depends on each site’s load, tariff, roof, supply and operating hours.',
        actions: [
          { label: 'Explore industry solutions', href: routes.industries, primary: true },
          { label: 'Qualify a business site', command: 'assessment' },
          { label: 'Discuss a rollout', topic: 'callback-commercial' }
        ]
      },
      safety: {
        message: 'Battery and inverter location must be assessed against the equipment instructions, electrical and building requirements, access, impact risks, ventilation and emergency considerations. Do not use chatbot guidance as installation approval.',
        actions: [
          { label: 'Request a site assessment', topic: 'callback', primary: true },
          { label: 'Dyness information', href: routes.dyness }
        ]
      },
      emergency: {
        message: 'If there is smoke, fire, sparking, electric shock or immediate electrical danger, move to a safe location and call 000. Do not approach or handle the equipment. This automated assistant is not an emergency-support service.',
        actions: []
      },
      privacy: {
        message: 'Do not enter retailer passwords, payment-card details or sensitive account credentials here. The assessment form asks only for practical project details and contact information so the team can respond.',
        actions: [
          { label: 'Connect data securely', href: routes.data, primary: true },
          { label: 'Request a callback', topic: 'callback' }
        ]
      },
      callback: {
        message: 'I can pass your enquiry to the Moebotics Energy team. The assessment form requires your name, phone and email so a specialist can respond; keep account passwords and payment details private.',
        actions: [
          { label: 'Open assessment form', callback: true, context: 'Website Smart Energy Assistant enquiry', primary: true },
          { label: 'Run guided assessment first', command: 'assessment' }
        ]
      },
      'callback-commercial': {
        message: 'I can open the commercial assessment form for a site, portfolio or national rollout. Add the organisation, locations, approximate energy spend and project goals in the context field.',
        actions: [
          { label: 'Open commercial form', callback: true, audience: 'commercial', context: 'Website Smart Energy Assistant commercial or multi-site enquiry', primary: true },
          { label: 'Qualify the project first', command: 'assessment' }
        ]
      },
      more: {
        message: 'I can help qualify a project, guide you to a package, explain energy data and incentives, answer common solar or battery questions, or hand the conversation to our team.',
        actions: [
          { label: '2-minute assessment', command: 'assessment', primary: true },
          { label: 'Home packages', topic: 'home' },
          { label: 'Business solutions', topic: 'business' },
          { label: 'Commercial rollouts', topic: 'commercial' },
          { label: 'Energy data', topic: 'data' },
          { label: 'Speak to the team', topic: 'callback' }
        ]
      }
    };

    const emit = (eventName, details = {}) => {
      const payload = { event: `energy_assistant_${eventName}`, ...details };
      let analyticsAllowed = false;
      try {
        analyticsAllowed = window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === true;
      } catch (_) {}
      if (!analyticsAllowed) return;
      document.dispatchEvent(new CustomEvent('moebotics:energy-assistant', { detail: payload }));
      if (Array.isArray(window.dataLayer)) window.dataLayer.push(payload);
    };

    const scrollToLatest = () => {
      window.requestAnimationFrame(() => {
        log.scrollTop = log.scrollHeight;
      });
    };

    const setStatus = message => {
      if (!status) return;
      status.textContent = '';
      window.requestAnimationFrame(() => { status.textContent = message; });
    };

    const hideTeaser = (remember = false) => {
      if (!teaser) return;
      if (state.teaserTimer) {
        window.clearTimeout(state.teaserTimer);
        state.teaserTimer = null;
      }
      teaser.hidden = true;
      if (remember) {
        state.interacted = true;
        try { window.sessionStorage.setItem('meEnergyAssistantTeaserDismissed', 'true'); } catch (_) {}
      }
    };

    const getContext = () => {
      const path = window.location.pathname.toLowerCase();
      if (path.includes('industries')) return 'industries';
      if (path.includes('small-business')) return 'business';
      if (path.includes('energy-packages')) return 'home';
      if (path.includes('commercial')) return 'commercial';
      if (path.includes('connect-energy-data')) return 'data';
      if (/\/(risen|dyness|solis|solinteg)/.test(path)) return 'brands';
      return 'default';
    };

    const welcomeContent = () => {
      const context = getContext();
      if (context === 'industries') return {
        message: 'Tell me about your site and I’ll guide you to the most useful commercial next step.',
        actions: [
          { label: 'Qualify my business', command: 'assessment', primary: true },
          { label: 'Industry solutions', topic: 'industries' },
          { label: 'Multi-site rollout', topic: 'commercial' },
          { label: 'Speak to a specialist', topic: 'callback-commercial' }
        ]
      };
      if (context === 'home') return {
        message: 'I can help match a home pathway, explain battery options or check what information is needed before a final quote.',
        actions: [
          { label: 'Find my package', href: routes.home, primary: true },
          { label: '2-minute assessment', command: 'assessment' },
          { label: 'Battery only', topic: 'battery' },
          { label: 'Ask about rebates', topic: 'incentives' }
        ]
      };
      if (context === 'business' || context === 'commercial') return {
        message: 'I can qualify the site, explain the assessment process or connect you with our commercial energy team.',
        actions: [
          { label: 'Qualify my project', command: 'assessment', primary: true },
          { label: 'Small-business packages', topic: 'business' },
          { label: 'Commercial rollout', topic: 'commercial' },
          { label: 'Speak to a specialist', topic: 'callback-commercial' }
        ]
      };
      if (context === 'data') return {
        message: 'I can explain what bills and interval data reveal, then guide you to the right assessment pathway.',
        actions: [
          { label: 'Why interval data matters', topic: 'data', primary: true },
          { label: 'Qualify my project', command: 'assessment' },
          { label: 'Speak to the team', topic: 'callback' }
        ]
      };
      if (context === 'brands') return {
        message: 'I can explain how our equipment fits into a complete system, or guide you to a package and site assessment.',
        actions: [
          { label: 'Compare pathways', command: 'assessment', primary: true },
          { label: 'Equipment brands', topic: 'brands' },
          { label: 'Home packages', topic: 'home' },
          { label: 'Business solutions', topic: 'business' }
        ]
      };
      return {
        message: 'Hi — I’m the Moebotics Energy Assistant. I can qualify your project, guide you to a suitable starting package, explain solar and batteries, or connect you with our team. What would you like to do?',
        actions: [
          { label: '2-minute assessment', command: 'assessment', primary: true },
          { label: 'Home packages', topic: 'home' },
          { label: 'Business solutions', topic: 'business' },
          { label: 'Commercial rollout', topic: 'commercial' },
          { label: 'Ask a question', focusInput: true }
        ]
      };
    };

    const createAction = action => {
      let element;
      if (action.href) {
        element = document.createElement('a');
        element.href = action.href;
        element.dataset.assistantLink = 'true';
      } else {
        element = document.createElement('button');
        element.type = 'button';
        if (action.topic) element.dataset.assistantTopic = action.topic;
        if (action.command) element.dataset.assistantCommand = action.command;
        if (action.answerKey) {
          element.dataset.assistantAnswerKey = action.answerKey;
          element.dataset.assistantAnswerValue = action.answerValue;
        }
        if (action.focusInput) element.dataset.assistantFocusInput = 'true';
        if (action.callback) {
          element.classList.add('js-open-diagnostic');
          element.dataset.assistantCallback = 'true';
          if (action.audience) element.dataset.openAudience = action.audience;
          element.dataset.openContext = action.context || 'Website Smart Energy Assistant enquiry';
        }
      }
      element.textContent = action.label;
      if (action.primary) element.classList.add('me2-assistant__action--primary');
      if (action.quiet) element.classList.add('me2-assistant__action--quiet');
      return element;
    };

    const appendMessage = (message, sender, actions = [], options = {}) => {
      const wrapper = document.createElement('div');
      wrapper.className = `me2-assistant__message me2-assistant__message--${sender}`;

      const speaker = document.createElement('span');
      speaker.className = 'me2-assistant__speaker';
      speaker.textContent = sender === 'bot' ? 'Moebotics Assistant' : 'You';

      const bubble = document.createElement('div');
      bubble.className = 'me2-assistant__bubble';
      const paragraph = document.createElement('p');
      paragraph.textContent = message;
      bubble.appendChild(paragraph);

      wrapper.append(speaker, bubble);
      if (options.badge) {
        const badge = document.createElement('span');
        badge.className = 'me2-assistant__badge';
        badge.textContent = options.badge;
        bubble.prepend(badge);
      }
      if (actions.length) {
        const actionGroup = document.createElement('div');
        actionGroup.className = 'me2-assistant__actions';
        actionGroup.setAttribute('aria-label', 'Response options');
        actions.forEach(action => actionGroup.appendChild(createAction(action)));
        wrapper.appendChild(actionGroup);
      }
      log.appendChild(wrapper);
      state.sessionMessages += 1;
      scrollToLatest();
      if (sender === 'bot') setStatus('New response from the Moebotics Energy Assistant');
      return wrapper;
    };

    const appendAssessmentSummary = summary => {
      const wrapper = document.createElement('div');
      wrapper.className = 'me2-assistant__message me2-assistant__message--bot';
      const speaker = document.createElement('span');
      speaker.className = 'me2-assistant__speaker';
      speaker.textContent = 'Moebotics Assistant';
      const card = document.createElement('div');
      card.className = 'me2-assistant__result';
      const badge = document.createElement('span');
      badge.className = 'me2-assistant__badge';
      badge.textContent = 'Preliminary pathway';
      const heading = document.createElement('h3');
      heading.textContent = summary.title;
      const body = document.createElement('p');
      body.textContent = summary.body;
      const list = document.createElement('dl');
      summary.details.forEach(([label, value]) => {
        const dt = document.createElement('dt');
        dt.textContent = label;
        const dd = document.createElement('dd');
        dd.textContent = value;
        list.append(dt, dd);
      });
      const note = document.createElement('p');
      note.className = 'me2-assistant__result-note';
      note.textContent = 'This is an initial pathway, not a design or quote. Final sizing and price require bills or interval data, equipment checks and a complete site assessment.';
      card.append(badge, heading, body, list, note);
      wrapper.append(speaker, card);

      const actions = document.createElement('div');
      actions.className = 'me2-assistant__actions';
      summary.actions.forEach(action => actions.appendChild(createAction(action)));
      wrapper.appendChild(actions);
      log.appendChild(wrapper);
      state.sessionMessages += 1;
      scrollToLatest();
      setStatus('Your preliminary pathway is ready');
    };

    const setProgress = (current = 0, total = assessmentOrder.length) => {
      if (!progress || !progressBar || !progressLabel) return;
      if (!current) {
        progress.hidden = true;
        progress.setAttribute('aria-valuenow', '0');
        progress.setAttribute('aria-valuetext', 'Assessment not active');
        return;
      }
      progress.hidden = false;
      progressBar.style.width = `${Math.min(100, Math.round((current / total) * 100))}%`;
      progressLabel.textContent = `Assessment ${current} of ${total}`;
      progress.setAttribute('aria-valuenow', String(current));
      progress.setAttribute('aria-valuetext', `Question ${current} of ${total}`);
    };

    const resetConversation = (announce = false) => {
      state.assessment = null;
      state.unsupportedCount = 0;
      state.sessionMessages = 0;
      log.innerHTML = '';
      setProgress(0);
      const welcome = welcomeContent();
      appendMessage(welcome.message, 'bot', welcome.actions);
      if (announce) setStatus('Conversation restarted');
      if (announce) emit('restart', { page_context: getContext() });
    };

    const questionOptions = question => {
      const answers = state.assessment.answers;
      const raw = typeof question.options === 'function' ? question.options(answers) : question.options;
      return raw.map(([label, value]) => ({
        label,
        answerKey: question.key,
        answerValue: value
      }));
    };

    const askAssessmentQuestion = () => {
      const index = state.assessment.index;
      const questionId = assessmentOrder[index];
      const question = assessmentQuestions[questionId];
      const prompt = typeof question.question === 'function'
        ? question.question(state.assessment.answers)
        : question.question;
      setProgress(index + 1);
      appendMessage(prompt, 'bot', questionOptions(question), { badge: 'Guided assessment' });
    };

    const startAssessment = () => {
      log.querySelectorAll('[data-assistant-answer-key]:not([disabled])').forEach(option => {
        option.disabled = true;
        option.setAttribute('aria-disabled', 'true');
      });
      state.assessment = { index: 0, answers: {}, labels: {} };
      appendMessage('Let’s build a useful starting brief. Six quick questions — no account passwords or payment details.', 'bot', [], { badge: 'About 2 minutes' });
      askAssessmentQuestion();
      emit('assessment_start', { page_context: getContext() });
    };

    const assessmentContext = answers => [
      'Smart Energy Assistant preliminary brief',
      `Site: ${answers.audience || 'not provided'}`,
      `Priority: ${answers.goal || 'not provided'}`,
      `Existing system: ${answers.existing || 'not provided'}`,
      `Energy information: ${answers.data || 'not provided'}`,
      `Site detail: ${answers.detail || 'not provided'}`,
      `Supply: ${answers.supply || 'not provided'}`
    ].join('. ');

    const buildAssessmentSummary = answers => {
      const context = assessmentContext(answers);
      if (answers.audience === 'home') {
        const batteryOnly = answers.goal === 'battery' && (answers.existing === 'solar only' || answers.existing === 'solar and battery');
        return {
          title: batteryOnly ? 'Start with the home battery pathway' : 'Start with the home package selector',
          body: batteryOnly
            ? 'Your answers point to a battery-focused review of existing solar, inverter compatibility, evening demand and backup priorities.'
            : 'Your answers are suited to our home selector, followed by bill and site verification before final design.',
          details: [
            ['Priority', answers.goal],
            ['Current system', answers.existing],
            ['Home', answers.detail],
            ['Supply', answers.supply],
            ['Available data', answers.data]
          ],
          actions: [
            { label: batteryOnly ? 'Open battery pathway' : 'Open home selector', href: routes.home, primary: true },
            { label: 'Send brief to our team', callback: true, audience: 'residential', context },
            ...(answers.data === 'not ready' ? [{ label: 'Connect energy data', href: routes.data }] : [])
          ]
        };
      }
      if (answers.audience === 'small-business') {
        return {
          title: 'Start with the small-business selector',
          body: 'Your answers provide a useful starting profile for solar, battery, demand, resilience and future-load screening up to the selector’s range.',
          details: [
            ['Priority', answers.goal],
            ['Current system', answers.existing],
            ['Operating profile', answers.detail],
            ['Supply', answers.supply],
            ['Available data', answers.data]
          ],
          actions: [
            { label: 'Open business selector', href: routes.business, primary: true },
            { label: 'Send brief to our team', callback: true, audience: 'commercial', context },
            { label: 'Explore industries', href: routes.industries }
          ]
        };
      }
      return {
        title: 'Start with a commercial portfolio assessment',
        body: 'Your project needs a structured review of sites, data, tariffs, network and site constraints, delivery priorities, reporting and investment criteria.',
        details: [
          ['Priority', answers.goal],
          ['Current system', answers.existing],
          ['Scale', answers.detail],
          ['Supply', answers.supply],
          ['Available data', answers.data]
        ],
        actions: [
          { label: 'Send brief to a specialist', callback: true, audience: 'commercial', context, primary: true },
          { label: 'Commercial capabilities', href: routes.commercial },
          { label: 'Connect energy data', href: routes.data }
        ]
      };
    };

    const handleAssessmentAnswer = button => {
      if (!state.assessment) return;
      const key = button.dataset.assistantAnswerKey;
      const expectedKey = assessmentOrder[state.assessment.index];
      if (!expectedKey || key !== expectedKey) return;
      const value = button.dataset.assistantAnswerValue;
      const label = button.textContent.trim();
      button.closest('.me2-assistant__actions')?.querySelectorAll('button').forEach(option => {
        option.disabled = true;
        option.setAttribute('aria-disabled', 'true');
      });
      state.assessment.answers[key] = value;
      state.assessment.labels[key] = label;
      appendMessage(label, 'user');
      state.assessment.index += 1;
      emit('assessment_answer', { question: key, answer: value });
      if (state.assessment.index < assessmentOrder.length) {
        askAssessmentQuestion();
        return;
      }
      const completedAnswers = { ...state.assessment.answers };
      const summary = buildAssessmentSummary(completedAnswers);
      const resultAudience = completedAnswers.audience;
      state.assessment = null;
      setProgress(0);
      appendAssessmentSummary(summary);
      emit('assessment_complete', { audience: resultAudience, goal: completedAnswers.goal });
    };

    const showConversation = topic => {
      const conversation = knowledge[topic] || knowledge.more;
      appendMessage(conversation.message, 'bot', conversation.actions);
      emit('topic', { topic });
    };

    const routeQuestion = rawQuestion => {
      const question = rawQuestion.toLowerCase().replace(/[^a-z0-9$\s+.-]/g, ' ');
      const has = pattern => pattern.test(question);
      if (has(/\b(fire|smoke|sparks?|burning|electric shock|electrocut|electrical danger)\b/)) return 'emergency';
      if (has(/\b(password|privacy|credit card|payment details|login|credentials?)\b/)) return 'privacy';
      if (has(/\b(start|begin|run|take) (the )?(guided |2[- ]?minute )?assessment\b|^assessment$/)) return 'start-assessment';
      if (has(/\b(start over|restart|reset|new conversation)\b/)) return 'restart';
      if (has(/\b(callback|call back|call me|ring me|contact|phone|telephone|human|person|sales|speak|talk to|assessment|specialist)\b/)) return 'callback';
      if (has(/\b(cashback|cash back)\b|\$?500\b/)) return 'cashback';
      if (has(/\b(savings?|roi|return on investment|payback|price|pricing|cost|quote|finance|ppa|bill reduction|zero bill)\b/)) return 'savings';
      if (has(/\b(single[- ]?phase|three[- ]?phase|3[- ]?phase|1[- ]?phase|supply phase)\b/)) return 'phase';
      if (has(/\b(rebate|rebates|stc|stcs|prc|prcs|vpp|incentive|grant|subsid|certificate)\b/)) return 'incentives';
      if (has(/\b(interval|cdr|consumer data right|energy data|smart meter|meter data|nmi|electricity bill|usage data|export data|upload bill)\b/)) return 'data';
      if (has(/\b(risen|dyness|solis|solinteg|stack100|stack 100|panel brand|inverter brand|battery brand|equipment)\b/)) return 'brands';
      if (has(/\b(ev|electric car|electric vehicle|charger|charging|fleet|electrification)\b/)) return 'ev';
      if (has(/\b(monitor|monitoring|optimis|reporting|managed service|after sales|after-sales|performance review)\b/)) return 'monitoring';
      if (has(/\b(safety|safe|placement|install location|fire wall|clearance|behind air con|near gate)\b/)) return 'safety';
      if (has(/\b(estimate|estimated|final price|additional charges|site inspection|structural engineer|cabling|da approval|development approval)\b/)) return 'estimate';
      if (has(/\b(national|multi[- ]?site|portfolio|large commercial|industrial|corporate rollout|enterprise|chain of stores)\b/)) return 'commercial';
      if (has(/\b(industry|industries|logistics|distribution|supermarket|iga|mitre 10|total tools|factory|manufacturing|restaurant|petrol station|strata|warehouse|cold storage|refrigeration)\b/)) return 'industries';
      if (has(/\b(small business|business|commercial|shop|office|warehouse|factory|100\s*kw|200\s*kwh)\b/)) return 'business';
      if (has(/\b(battery only|battery|storage|blackout|backup|back-up|outage)\b/)) return 'battery';
      if (has(/\b(solar|panel|roof|generation|export)\b/)) return 'solar';
      if (has(/\b(home|house|residential|bedroom|family)\b/)) return 'home';
      if (has(/\b(more|options|help|menu|what can you do|start over)\b/)) return 'more';
      return 'fallback';
    };

    const answerQuestion = question => {
      appendMessage(question, 'user');
      const topic = routeQuestion(question);
      emit('question', { recognised_topic: topic, length: question.length });
      if (topic === 'start-assessment') {
        startAssessment();
        return;
      }
      if (topic === 'restart') {
        resetConversation(true);
        return;
      }
      if (topic !== 'fallback') {
        state.unsupportedCount = 0;
        showConversation(topic);
        return;
      }

      state.unsupportedCount += 1;
      const message = state.unsupportedCount > 1
        ? 'I’m not confident enough to answer that accurately. Please search the site or pass the question to a Moebotics Energy specialist.'
        : 'I’m not confident enough to answer that accurately. I can qualify your project, explain common solar and battery topics, search the site or connect you with our team.';
      appendMessage(message, 'bot', [
        { label: 'Open site search', href: routes.search },
        { label: '2-minute assessment', command: 'assessment' },
        { label: 'Ask a specialist', topic: 'callback', primary: state.unsupportedCount > 1 }
      ]);
    };

    const setMobilePageState = isOpen => {
      const mobile = mobileMedia.matches;
      document.body.classList.toggle('me2-assistant-open', isOpen && mobile);
      panel.setAttribute('aria-modal', isOpen && mobile ? 'true' : 'false');
      if (isOpen && mobile) {
        document.querySelectorAll('.me2-heat-alert, [data-site-header], #MainContent, .me2-site-footer').forEach(element => {
          if (element.contains(root) || element.hasAttribute('inert')) return;
          element.setAttribute('inert', '');
          state.inertedElements.add(element);
        });
      } else {
        state.inertedElements.forEach(element => element.removeAttribute('inert'));
        state.inertedElements.clear();
      }
    };

    const openPanel = (source = 'launcher') => {
      state.interacted = true;
      state.lastFocused = document.activeElement instanceof HTMLElement ? document.activeElement : launcher;
      panel.removeAttribute('hidden');
      launcher.setAttribute('aria-expanded', 'true');
      hideTeaser(false);
      setMobilePageState(true);
      closeButton.focus();
      scrollToLatest();
      emit('open', { source, page_context: getContext() });
    };

    const closePanel = (restoreFocus = true) => {
      panel.setAttribute('hidden', '');
      launcher.setAttribute('aria-expanded', 'false');
      setMobilePageState(false);
      if (restoreFocus) {
        const target = state.lastFocused && document.contains(state.lastFocused) && state.lastFocused.offsetParent !== null && !state.lastFocused.closest('[hidden]')
          ? state.lastFocused
          : launcher;
        target.focus();
      }
      emit('close', { message_count: state.sessionMessages });
    };

    launcher.addEventListener('click', () => {
      if (panel.hasAttribute('hidden')) openPanel('launcher');
      else closePanel();
    });
    closeButton.addEventListener('click', () => closePanel());
    resetButton?.addEventListener('click', () => resetConversation(true));
    teaserOpen?.addEventListener('click', () => openPanel('teaser'));
    teaserClose?.addEventListener('click', () => hideTeaser(true));

    root.addEventListener('click', event => {
      const answerButton = event.target.closest('[data-assistant-answer-key]');
      if (answerButton && root.contains(answerButton)) {
        handleAssessmentAnswer(answerButton);
        return;
      }

      const commandButton = event.target.closest('[data-assistant-command]');
      if (commandButton && root.contains(commandButton)) {
        appendMessage(commandButton.textContent.trim(), 'user');
        if (commandButton.dataset.assistantCommand === 'assessment') startAssessment();
        return;
      }

      const topicButton = event.target.closest('[data-assistant-topic]');
      if (topicButton && root.contains(topicButton)) {
        appendMessage(topicButton.textContent.trim(), 'user');
        showConversation(topicButton.dataset.assistantTopic);
        return;
      }

      const inputButton = event.target.closest('[data-assistant-focus-input]');
      if (inputButton && root.contains(inputButton)) {
        input.focus();
        return;
      }

      const callbackButton = event.target.closest('[data-assistant-callback]');
      if (callbackButton && root.contains(callbackButton)) {
        const diagnosticModal = document.querySelector('#diagnosticModal');
        if (!diagnosticModal) {
          event.stopPropagation();
          appendMessage('The assessment form is temporarily unavailable here. Please use the contact page and our team will help with your enquiry.', 'bot', [
            { label: 'Open contact page', href: routes.contact, primary: true }
          ]);
          return;
        }
        const handoffAudience = callbackButton.dataset.openAudience || 'unspecified';
        let handoffConfirmed = false;
        let recoveryTimer;
        const confirmHandoff = () => {
          const opened = diagnosticModal.classList.contains('open') || diagnosticModal.getAttribute('aria-hidden') === 'false';
          if (!opened || handoffConfirmed) return;
          handoffConfirmed = true;
          observer.disconnect();
          window.clearTimeout(recoveryTimer);
          emit('lead_form_open', { audience: handoffAudience });
        };
        const observer = new MutationObserver(confirmHandoff);
        observer.observe(diagnosticModal, { attributes: true, attributeFilter: ['class', 'aria-hidden'] });
        closePanel(false);
        launcher.focus();
        window.queueMicrotask(confirmHandoff);
        recoveryTimer = window.setTimeout(() => {
          observer.disconnect();
          if (handoffConfirmed) return;
          openPanel('handoff-recovery');
          appendMessage('I could not open the assessment form. Please use the contact page and our team will help with your enquiry.', 'bot', [
            { label: 'Open contact page', href: routes.contact, primary: true }
          ]);
        }, 1200);
        return;
      }

      const link = event.target.closest('[data-assistant-link]');
      if (link && root.contains(link)) {
        let destination = 'site-route';
        try { destination = new URL(link.href, window.location.origin).pathname; } catch (_) {}
        emit('route_click', { destination });
      }
    });

    form.addEventListener('submit', event => {
      event.preventDefault();
      const question = input.value.trim();
      if (!question) return;
      input.value = '';
      answerQuestion(question);
    });

    document.addEventListener('keydown', event => {
      if (panel.hasAttribute('hidden')) return;
      if (event.defaultPrevented || document.body.classList.contains('modal-open')) return;
      if (event.key === 'Escape') {
        if (!panel.contains(document.activeElement)) return;
        closePanel();
        return;
      }
      if (event.key !== 'Tab' || panel.getAttribute('aria-modal') !== 'true') return;
      const focusable = [...panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])')]
        .filter(element => element.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    const handleMobileChange = () => {
      if (!panel.hasAttribute('hidden')) setMobilePageState(true);
    };
    if (mobileMedia.addEventListener) mobileMedia.addEventListener('change', handleMobileChange);
    else mobileMedia.addListener?.(handleMobileChange);

    const pageDiagnosticModal = document.querySelector('#diagnosticModal');
    if (pageDiagnosticModal) {
      const pageModalObserver = new MutationObserver(() => {
        const opened = pageDiagnosticModal.classList.contains('open') || pageDiagnosticModal.getAttribute('aria-hidden') === 'false';
        if (opened && !panel.hasAttribute('hidden')) closePanel(false);
      });
      pageModalObserver.observe(pageDiagnosticModal, { attributes: true, attributeFilter: ['class', 'aria-hidden'] });
    }

    resetConversation(false);
    root.dataset.assistantReady = 'true';
    root.dataset.assistantBound = 'true';

    try {
      const dismissed = window.sessionStorage.getItem('meEnergyAssistantTeaserDismissed') === 'true';
      if (!dismissed && teaser) {
        state.teaserTimer = window.setTimeout(() => {
          state.teaserTimer = null;
          if (!state.interacted && panel.hasAttribute('hidden')) teaser.hidden = false;
        }, 9000);
      }
    } catch (_) {}
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initEnergyAssistant, { once: true });
  } else {
    initEnergyAssistant();
  }
})();
